import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { useEffect, useState } from "react";
import apiService from "../../services/apiService";

const schema = z.object({
  institution: z.string().min(1, "Required"),
  lenderName: z.string().min(1, "Required"),
  programLimit: z.coerce.number().positive("Required"),
  applicationRM: z.string().min(1, "Required"),
  note: z.string().optional(),
});

export type AddLenderValues = z.infer<typeof schema>;

interface LenderUsers {
  name: string;
  id: number;
}

interface LenderUserListResponse {
  data: LenderUsers[];
}

interface LenderCompanyList {
  id: number;
  name: string;
}

interface AddLenderDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSubmit: (values: AddLenderValues) => void;
  initialValues?: Partial<AddLenderValues>;
  title?: string;
  institutions: LenderCompanyList[];
  admin_user_list: AdminUserList[];
}
interface AdminUserList {
  id: number;
  name: string;
};



export function AddLenderDialog({
  open,
  onOpenChange,
  onSubmit,
  initialValues,
  title,
  institutions,
  admin_user_list
}: AddLenderDialogProps) {
  const [lenderUsers, setLenderUsers] = useState<LenderUsers[]>([]);
  const [loadingLenders, setLoadingLenders] = useState(false);

  const form = useForm<AddLenderValues>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: (initialValues as any) ?? {
      institution: "",
      lenderName: "",
      programLimit: undefined,
      applicationRM: "",
      note: ""
    }
  });

  useEffect(() => {
    if (open) {
      form.reset({
        institution: "",
        lenderName: "",
        programLimit: undefined,
        applicationRM: "",
        note: ""
      });
      setLenderUsers([]); // ✅ clear lender users list when reopening
    }
  }, [open, form]);

  const handleInstitutionChange = async (value: string) => {
    const selected = institutions.find((inst) => String(inst.id) === value);
    if (selected) {
      await fetchLenderUserListbyLenderId(selected.id);
    }
  };

  const fetchLenderUserListbyLenderId = async (lender_company_id: number) => {
    try {
      setLoadingLenders(true);
      const response = await apiService.get<LenderUserListResponse>(
        `/customers/get-lender-user-list/${lender_company_id}`
      );
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id
      }));
      setLenderUsers(transformedDocs);
    } catch (error) {
      console.error("Error fetching lender users:", error);
    } finally {
      setLoadingLenders(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title ?? "Tag Lender"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((vals) => onSubmit(vals))}
            className="space-y-5"
          >
            {/* Lending Institution Select */}
            <FormField
              control={form.control}
              name="institution"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lending Institution *</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleInstitutionChange(value);
                      form.setValue("lenderName", ""); // reset lenderName
                    }}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select institution" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {institutions.map((i) => (
                        <SelectItem key={i.id} value={i.id.toString()}>
                          {i.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Lender Name Select */}
            <FormField
              control={form.control}
              name="lenderName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lender Name *</FormLabel>
                  <FormControl>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={loadingLenders || lenderUsers.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingLenders
                              ? "Loading..."
                              : lenderUsers.length > 0
                              ? "Select lender name"
                              : "No lenders available"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {lenderUsers.map((user) => (
                          <SelectItem key={user.id} value={String(user.id)}>
                            {user.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Program Limit & Application RM */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="programLimit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Program Limit / Quantum *</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Amount" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* <FormField
                control={form.control}
                name="applicationRM"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application RM</FormLabel>
                    <FormControl>
                      <Input placeholder="Optional" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}


<FormField
                control={form.control}
                name="applicationRM"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application RM *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select RM" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {admin_user_list?.map((rm) => (
                           <SelectItem key={rm.id} value={String(rm.id)}>{rm.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <FormMessage />
                  </FormItem>
                )}
              />

            </div>

            {/* Note */}
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="submit"
                disabled={!form.formState.isValid || form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
